import { TagInput } from '@morressier/alembic';
import { useEffect, useMemo, useRef } from 'react';
import { useForm, useWatch } from 'react-hook-form';

type TagFormValue = { tags: { text: string }[] };

interface Props {
  value: string[];
  onChange: (value: string[]) => void;
  isInvalid?: boolean;
  placeholder?: string;
}

/** Caps how many values one criterion can carry, to bound the query it builds. */
const MAX_VALUES = 10;

const toTagFormValue = (values: string[]): TagFormValue => ({
  tags: values.map(text => ({ text }))
});

/**
 * Trimmed values, with blanks and case-insensitive duplicates collapsed away —
 * neither adds anything to the search, and a blank would widen it. Exported so
 * the panel normalizes the values it submits the same way the chips do.
 */
export const normalizeValues = (values: string[]): string[] => {
  const seen = new Set<string>();

  return values.reduce<string[]>((acc, value) => {
    const trimmed = value.trim();
    const key = trimmed.toLowerCase();

    if (!trimmed || seen.has(key)) {
      return acc;
    }

    seen.add(key);
    acc.push(trimmed);

    return acc;
  }, []);
};

const arraysMatch = (left: string[], right: string[]) =>
  left.length === right.length && left.every((item, i) => item === right[i]);

/**
 * Chip input for fields that accept several values (keywords and the like).
 *
 * `TagInput` reads and writes a react-hook-form array, so it needs a form of
 * its own; this component keeps that inner form in step with the `value` the
 * panel owns. The panel stays the source of truth: the inner form is only
 * reset when `value` diverges from what the tags already hold (a field change,
 * or a saved filter loading), never for the change this component just
 * reported.
 */
const MultiValueInput = ({
  value,
  onChange,
  isInvalid,
  placeholder
}: Props) => {
  const methods = useForm<TagFormValue>({
    defaultValues: toTagFormValue(value)
  });

  const tags = useWatch({ control: methods.control, name: 'tags' });

  const values = useMemo(
    () => normalizeValues((tags ?? []).map(tag => tag?.text ?? '')),
    [tags]
  );

  const reportedRef = useRef(value);

  useEffect(() => {
    if (!arraysMatch(reportedRef.current, value)) {
      reportedRef.current = value;
      methods.reset(toTagFormValue(value));
    }
  }, [value, methods]);

  useEffect(() => {
    if (arraysMatch(values, reportedRef.current)) {
      return;
    }

    reportedRef.current = values;
    onChange(values);
  }, [values, onChange]);

  return (
    <TagInput
      control={methods.control}
      name="tags"
      isInvalid={isInvalid}
      placeholder={placeholder ?? 'Type a value and press Enter'}
      tagLimit={{ maximum: MAX_VALUES }}
    />
  );
};

export default MultiValueInput;
